<template>
  <fragment v-if="expanded">
    <!-- Second level categories, items like "FourStroke" or "ProKicker" -->
    <el-menu-item-group :class="itemClass" v-for="(category, categoryKey) in childCategories" :key="`c-${categoryKey}`"
      :index="`${indexPrefix}-c-${categoryKey}`">
      <template slot="title">{{ category.title }}</template>
      <!-- Third level products -->
      <el-menu-item v-for="(product, productKey) in category.products" :key="productKey"
        :route="productDetailRoute(product)"
        :index="`${indexPrefix}-c-${categoryKey}-p-${product.id}`" :class="`nested ${itemClass ? itemClass : ''}`">
        {{ product.trimmedProductTitle }}
      </el-menu-item>
    </el-menu-item-group>
    <!-- Second level products when sub-sub categories don't exist -->
    <el-menu-item class="second-level-product" v-for="(product, productKey) in products" :key="`p-${productKey}`"
      :route="productDetailRoute(product)" :index="`${indexPrefix}-p${product.id}`"
      :class="itemClass">
      {{ product.title }}
    </el-menu-item>
  </fragment>
</template>

<script>
import products from '@/components/products';
import mixins from '@/mixins';
import { mapGetters } from 'vuex';
import { Fragment } from 'vue-frag';
import { buildProductDetailRoute } from '@/components/products/product.util';

export default {
  name: 'NavCategorizedProducts',
  mixins: [
    mixins.supportedFeatures
  ],
  props: {
    category: {
      type: Object,
      required: true,
    },
    expanded: {
      type: Boolean,
      required: true,
    },
    indexPrefix: {
      type: String,
      required: true,
    },
    itemClass: {
      type: String,
      required: false,
    }
  },
  data: () => {
    return {
    }
  },
  computed: {
    ...mapGetters(products.store.namespace, [
      'getMenuProductsByCategory',
      'hasMenuProductsInCategory',
    ]),
    hasProducts() {
      return this.hasMenuProductsInCategory(this.category.id);
    },
    products() {
      return this.getMenuProductsByCategory(this.category.id) || [];
    },
    childCategories() {
      const children = [];
      if (this.category.children) {
        this.category.children.forEach((childCategory) => {
          // Make a clone to ensure we're not modifying vuex store data
          let childCategoryClone = { ...childCategory };
          childCategoryClone.products = [];
          let products = this.getMenuProductsByCategory(childCategoryClone.id);
          products.forEach((product) => {
            // Make a clone to ensure we're not modifying vuex store data
            let productClone = { ...product };
            productClone.trimmedProductTitle = this.removeCategoryTitle(productClone.title, childCategoryClone.title);
            childCategoryClone.products.push(productClone);
          });
          if (childCategoryClone.products.length > 0) {
            childCategoryClone.hasProducts = true;
            children.push(childCategoryClone);
          }
        });
      }
      return children;
    },
  },
  methods: {
    productDetailRoute(product) {
      return buildProductDetailRoute(product || {});
    },
    removeCategoryTitle(productTitle, categoryTitle) {
      const pluralTitle = `${categoryTitle}s`
      return productTitle.
        replace(pluralTitle, '').
        replace(categoryTitle, '');
    },
  },
  components: {
    Fragment,
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/base/_variables.scss';
@import '@/assets/base/_typography.scss';
@import 'element-theme-chalk/src/mixins/mixins.scss';


.el-menu-item {
  @extend h4;
  font-family: $font-family-headings;
  line-height: 40px;
  color: $--color-menu-gray;
  border-bottom: 1px solid $--color-grayscale-2 !important;

  &:focus-visible {
    text-decoration: underline;
  }
}



.el-menu-item-group {
  &:deep(.el-menu-item-group__title) {
    @extend h4;
    font-family: $font-family-headings;
    line-height: 40px;
    color: $--color-secondary;
    padding: 0.5rem 0.5rem 0 0.25rem !important;
    white-space: normal;

  }

  &:deep(.el-menu-item.nested) {
    white-space: normal;
    padding: 0.5rem 30px 1rem 1rem !important;
    margin: 0.5rem 0 0 0 !important;
    height: auto;
  }

  &.mobile {
    &:deep(.el-menu-item-group__title) {
      padding-left: 20px !important;
    }

    &:deep(.el-menu-item) {
      padding-left: 40px !important;
    }
  }

}
</style>
